import { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Tabs,
    Tab,
    Alert,
    CircularProgress,
    Button,
    Snackbar,
} from '@mui/material';
import {
    Label as ThemesIcon,
    ViewColumn as ColumnsIcon,
    Security as PiiIcon,
    TableRows as TableExclusionsIcon,
    FilterAlt as DedupIcon,
    AccountTree as SchemaMapperIcon,
    CloudUpload as FilesIcon,
    AutoAwesome as AutoGapIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { getGlobalConfig } from '../services/api';
import ThemeEditor from '../components/config/ThemeEditor';
import FieldListEditor from '../components/config/FieldListEditor';
import TableExclusionsConfig from '../components/config/TableExclusionsConfig';
import DeduplicationConfig from '../components/config/DeduplicationConfig';
import SchemaMapperPriorityConfig from '../components/config/SchemaMapperPriorityConfig';
import ReportMatchingAutoRecommendationConfig from '../components/config/ReportMatchingAutoRecommendationConfig';
import SharedFilesManager from '../components/config/SharedFilesManager';

/**
 * Global Configuration Page
 *
 * Allows users to manage:
 * - Themes (legacy and catalyst)
 * - Common columns to ignore
 * - PII fields
 * - Deduplication rules for lineage upload
 */
function GlobalConfigPage() {
    const [activeTab, setActiveTab] = useState(0);
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const sharedFilesEnabled = String(import.meta.env.VITE_ENABLE_SHARED_FILES || '')
        .toLowerCase() === 'true';

    // Fetch configuration on mount
    useEffect(() => {
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getGlobalConfig();
            setConfig(data);
        } catch (err) {
            setError('Failed to load configuration. Please ensure the backend is running.');
            console.error('Error fetching config:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const showNotification = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleConfigUpdate = (updatedConfig) => {
        setConfig(updatedConfig);
        showNotification('Configuration saved successfully');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error" action={
                    <Button color="inherit" size="small" onClick={fetchConfig}>
                        Retry
                    </Button>
                }>
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            {/* Page Header */}
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom fontWeight={700} color="text.primary">
                        Configurations
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Manage themes, common columns, PII fields, table exclusions, deduplication, schema-mapper priorities, and report-matching auto gap recommendation settings.
                    </Typography>
                </Box>
                <Button
                    variant="outlined"
                    startIcon={<RefreshIcon />}
                    onClick={fetchConfig}
                >
                    Refresh
                </Button>
            </Box>

            {/* Version Info */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                    Config Version: {config?.version || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    Last Updated: {config?.last_updated || 'N/A'}
                </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    sx={{
                        '& .MuiTab-root': {
                            minHeight: 56,
                        }
                    }}
                >
                    <Tab
                        icon={<ThemesIcon />}
                        iconPosition="start"
                        label="Themes"
                        id="config-tab-0"
                    />
                    <Tab
                        icon={<ColumnsIcon />}
                        iconPosition="start"
                        label="Common Columns"
                        id="config-tab-1"
                    />
                    <Tab
                        icon={<PiiIcon />}
                        iconPosition="start"
                        label="PII Fields"
                        id="config-tab-2"
                    />
                    <Tab
                        icon={<TableExclusionsIcon />}
                        iconPosition="start"
                        label="Table Exclusions"
                        id="config-tab-3"
                    />
                    <Tab
                        icon={<DedupIcon />}
                        iconPosition="start"
                        label="Deduplication"
                        id="config-tab-4"
                    />
                    <Tab
                        icon={<SchemaMapperIcon />}
                        iconPosition="start"
                        label="Schema Mapper"
                        id="config-tab-5"
                    />
                    <Tab
                        icon={<AutoGapIcon />}
                        iconPosition="start"
                        label="Auto Gap Recommendation"
                        id="config-tab-6"
                    />
                    {sharedFilesEnabled && (
                        <Tab
                            icon={<FilesIcon />}
                            iconPosition="start"
                            label="File Upload"
                            id="config-tab-7"
                        />
                    )}
                </Tabs>
            </Box>

            {/* Tab Panels */}
            <Box role="tabpanel" hidden={activeTab !== 0}>
                {activeTab === 0 && config && (
                    <ThemeEditor
                        legacyThemes={config.legacy}
                        catalystThemes={config.catalyst}
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 1}>
                {activeTab === 1 && config && (
                    <FieldListEditor
                        title="Common Columns"
                        description="Fields present in most reports - excluded from similarity calculations"
                        fields={config.common_fields}
                        fieldType="common-fields"
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 2}>
                {activeTab === 2 && config && (
                    <FieldListEditor
                        title="PII Fields"
                        description="Personally Identifiable Information - excluded from all comparisons"
                        fields={config.pii_fields}
                        fieldType="pii-fields"
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 3}>
                {activeTab === 3 && config && (
                    <TableExclusionsConfig
                        tableExclusions={config.table_exclusions}
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 4}>
                {activeTab === 4 && config && (
                    <DeduplicationConfig
                        deduplication={config.deduplication}
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 5}>
                {activeTab === 5 && config && (
                    <SchemaMapperPriorityConfig
                        priorities={config.schema_mapper_priorities}
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            <Box role="tabpanel" hidden={activeTab !== 6}>
                {activeTab === 6 && config && (
                    <ReportMatchingAutoRecommendationConfig
                        reportMatching={config.report_matching}
                        onUpdate={handleConfigUpdate}
                        showNotification={showNotification}
                    />
                )}
            </Box>

            {sharedFilesEnabled && (
                <Box role="tabpanel" hidden={activeTab !== 7}>
                    {activeTab === 7 && (
                        <SharedFilesManager showNotification={showNotification} />
                    )}
                </Box>
            )}

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default GlobalConfigPage;
